/**
 * Guided-tour engine provider. Mounts react-joyride ONCE (controlled mode —
 * we own `stepIndex`) inside the authenticated shell so a single tour can
 * navigate across routes between steps:
 *   - a route-sync effect `navigate()`s to the current step's route when
 *     off-route, and Joyride pauses off-route via `run={run && onRoute}`;
 *   - `?tour=welcome` deep-links autostart once auth has resolved (the param
 *     is stripped so a reload never restarts the tour);
 *   - a missing anchor (TARGET_NOT_FOUND after the 8s wait) auto-advances so
 *     the tour never wedges;
 *   - completion/dismissal + last step index persist per user in
 *     localStorage; a dismissed tour resumes where it left off;
 *   - on mobile the sidebar nav anchors live in a (usually closed) drawer
 *     and the desktop sidebar is display-hidden, so `nav-*` steps render as
 *     viewport-centered steps there.
 */
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import type { Step } from 'react-joyride'
import { useAuthStore } from '@/stores/useAuthStore'
import { useIsMobile } from '@/hooks/useMediaQuery'
import type { TourStep } from '@/lib/tours/types'
import { visibleSteps, nextSectionIndex } from '@/lib/tours/filter'
import { toJoyrideStep } from '@/lib/tours/joyride'
import { readTourProgress, writeTourProgress } from '@/lib/tours/storage'
import { welcomeTour } from '@/lib/tours/welcome'
import { TourContext } from './tour-context'
import type { TourContextValue } from './tour-context'

const JoyrideRunner = lazy(() => import('./JoyrideRunner'))

const TOURS = { [welcomeTour.id]: welcomeTour }

/** sessionStorage key holding a deep-linked tour id until auth settles. */
const PENDING_TOUR_KEY = 'maxflow-pending-tour'

export function TourProvider({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const user = useAuthStore((state) => state.user)
  const isAuthLoading = useAuthStore((state) => state.isLoading)
  const isMobile = useIsMobile()

  const [activeTourId, setActiveTourId] = useState<string | null>(null)
  const [steps, setSteps] = useState<TourStep[]>([])
  const [stepIndex, setStepIndex] = useState(0)
  const [run, setRun] = useState(false)

  const currentStep = steps[stepIndex] ?? null
  // Compared against pathname + search, not pathname alone — a step may
  // deep-link a query string onto a route to land on a specific in-page tab
  // (e.g. `/library?section=content`), and the querystring-less pathname
  // never equals that literal route string.
  const currentPath = `${location.pathname}${location.search}`
  const onRoute = currentStep ? currentPath === currentStep.route : false

  // Refs so the stable callbacks (Joyride events, tooltip buttons) read
  // current values without re-binding. Synced post-render in an effect —
  // events always fire after the render that produced them.
  const runRef = useRef(run)
  const stepsRef = useRef(steps)
  const stepIndexRef = useRef(stepIndex)
  const tourIdRef = useRef(activeTourId)
  const userIdRef = useRef(user?.id)
  const onRouteRef = useRef(onRoute)
  useEffect(() => {
    runRef.current = run
    stepsRef.current = steps
    stepIndexRef.current = stepIndex
    tourIdRef.current = activeTourId
    userIdRef.current = user?.id
    onRouteRef.current = onRoute
  })

  const finish = useCallback((outcome: 'completed' | 'dismissed') => {
    if (!runRef.current) return
    const tourId = tourIdRef.current
    const userId = userIdRef.current
    if (tourId && userId) {
      writeTourProgress(tourId, userId, {
        status: outcome,
        lastStepIndex: outcome === 'completed' ? 0 : stepIndexRef.current,
      })
    }
    setRun(false)
    setActiveTourId(null)
    setSteps([])
    setStepIndex(0)
  }, [])

  const startTour = useCallback((tourId: string) => {
    const tour = TOURS[tourId]
    if (!tour) return
    const visible = visibleSteps(tour)
    if (visible.length === 0) return
    // A tour dismissed mid-run resumes at the step it was left on.
    const saved = userIdRef.current ? readTourProgress(tourId, userIdRef.current) : null
    const resumeIndex =
      saved?.status === 'dismissed'
        ? Math.min(Math.max(saved.lastStepIndex, 0), visible.length - 1)
        : 0
    setActiveTourId(tourId)
    setSteps(visible)
    setStepIndex(resumeIndex)
    setRun(true)
  }, [])

  const next = useCallback(() => {
    const last = stepsRef.current.length - 1
    if (stepIndexRef.current >= last) {
      finish('completed')
      return
    }
    setStepIndex((i) => Math.min(i + 1, last))
  }, [finish])

  const back = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1))
  }, [])

  const skipSection = useCallback(() => {
    const idx = nextSectionIndex(stepsRef.current, stepIndexRef.current)
    if (idx === -1) {
      finish('completed')
      return
    }
    setStepIndex(idx)
  }, [finish])

  const endTour = useCallback(
    (outcome: 'dismissed' | 'completed' = 'dismissed') => finish(outcome),
    [finish]
  )

  // Cross-route navigation: move to the step's route before highlighting.
  useEffect(() => {
    if (!run || !currentStep) return
    if (currentPath !== currentStep.route) {
      navigate(currentStep.route)
    }
  }, [run, currentStep, currentPath, navigate])

  // Deep-link autostart (?tour=welcome), staged in two steps because on a
  // full page load ProtectedRoute swaps AppShell for a spinner while the
  // initial auth check runs — this provider unmounts and remounts around it,
  // so any tour started on the first mount is lost. Step 1 stashes the tour
  // id in sessionStorage and strips the URL param immediately (a reload or
  // back-navigation can never restart the tour). Step 2 starts the pending
  // tour once auth has settled; the stash survives the remount. When auth is
  // already settled (an in-app navigation), step 1 starts it directly.
  const authReady = !isAuthLoading && !!user?.id
  const startPendingTour = useCallback(() => {
    let tourId: string | null = null
    try {
      tourId = sessionStorage.getItem(PENDING_TOUR_KEY)
      if (tourId) sessionStorage.removeItem(PENDING_TOUR_KEY)
    } catch {
      return
    }
    if (tourId) startTour(tourId)
  }, [startTour])

  useEffect(() => {
    const tourId = searchParams.get('tour')
    if (!tourId) return
    try {
      sessionStorage.setItem(PENDING_TOUR_KEY, tourId)
    } catch {
      // Storage blocked — the tour deep link degrades to a no-op.
    }
    const params = new URLSearchParams(searchParams)
    params.delete('tour')
    setSearchParams(params, { replace: true })
    // The stash is consumed here at most once, so this cannot loop.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (authReady) startPendingTour()
  }, [searchParams, setSearchParams, authReady, startPendingTour])

  useEffect(() => {
    // The stashed deep link is an external event — starting the tour from
    // the effect that observes it is the sync point, not a render-loop
    // hazard (the stash is consumed, so this fires at most once).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (authReady) startPendingTour()
  }, [authReady, startPendingTour])

  // Joyride events arrive through JoyrideRunner. The run guard drops events
  // that land after the tour ended.
  const onFinish = useCallback(() => {
    if (runRef.current) finish('completed')
  }, [finish])
  const onDismiss = useCallback(() => {
    if (runRef.current) finish('dismissed')
  }, [finish])
  // Never stall on a genuinely-missing target — advance past it. Only when
  // already ON the step's route: off-route the target is absent simply because
  // navigation is still in flight (joyride is paused via run && onRoute) and
  // advancing would silently skip the step.
  const onTargetMissing = useCallback(() => {
    if (runRef.current && onRouteRef.current) next()
  }, [next])

  // On mobile the desktop sidebar (and its nav anchors) is display-hidden,
  // so nav-targeted steps become viewport-centered steps there.
  const joyrideSteps: Step[] = useMemo(
    () => steps.map((s) => toJoyrideStep(s, isMobile && s.target.startsWith('nav-'))),
    [steps, isMobile]
  )

  const value: TourContextValue = useMemo(
    () => ({
      isRunning: run,
      currentStep,
      stepIndex,
      stepCount: steps.length,
      hasNextSection: nextSectionIndex(steps, stepIndex) !== -1,
      startTour,
      next,
      back,
      skipSection,
      endTour,
    }),
    [run, currentStep, stepIndex, steps, startTour, next, back, skipSection, endTour]
  )

  return (
    <TourContext.Provider value={value}>
      {children}
      {steps.length > 0 && (
        <Suspense fallback={null}>
          <JoyrideRunner
            steps={joyrideSteps}
            run={run && onRoute}
            stepIndex={stepIndex}
            onFinish={onFinish}
            onDismiss={onDismiss}
            onTargetMissing={onTargetMissing}
          />
        </Suspense>
      )}
    </TourContext.Provider>
  )
}
